import { ChangeEvent, FC, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import IconBtn from 'modules/icon-btn/icon-btn';
import UiInput from 'modules/ui/ui-input/ui-input';
import { useAppDispatch } from 'store';
import { updateCompany } from 'store/actions/company-info';
import { RootState } from 'types';
import { useHistory } from 'react-router';
import './index.scss';
import { SmallKeyIcon } from 'assets/icons';

export type MainInfoFormState = {
  tin: string;
  shortName: string;
  fullName: string;
  address: string;
  regNumber: string;
  bankName: string;
  bik: string;
  accountNumber: string;
  corrNumber: string;
  bankTin: string;
};

const MainTab: FC = () => {
  const dispatch = useAppDispatch();
  const auth = useSelector((s: RootState) => s.auth.account);
  const company = useSelector(
    (s: RootState) => s.companiesInfo.companies[auth.id]
  );

  const bankCompany = useMemo(() => company?.accounts?.find((i) => i), [
    company,
  ]);
  const isAdmin =
    useSelector((s: RootState) => s.auth.profile.role) === 'ADMIN';

  const [readOnly, setReadOnly] = useState(true);
  const [loading, setLoading] = useState(false);

  const history = useHistory();

  const initialState: MainInfoFormState = {
    tin: company?.tin,
    shortName: company?.shortName,
    fullName: company?.fullName,
    address: company?.address,
    regNumber: company?.regNumber,
    bankName: bankCompany?.bankName || '',
    bik: bankCompany?.bik || '',
    accountNumber: bankCompany?.accountNumber || '',
    corrNumber: bankCompany?.corrAccountNumber || '',
    bankTin: bankCompany?.tin || '',
  };

  const formState = useFormik({
    initialValues: initialState,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values) => {
      if (
        values.tin &&
        values.shortName &&
        values.fullName &&
        values.address &&
        values.regNumber &&
        values.bankName &&
        values.bik &&
        values.accountNumber &&
        values.corrNumber &&
        values.bankTin
      ) {
        setLoading(true);
        dispatch(updateCompany({ ...values, orgId: company.id })).then(
          (res) => {
            if (res.payload) {
              setLoading(false);
              setReadOnly(true);
            }
          },
          () => {
            setLoading(false);
            setReadOnly(false);
          }
        );
      }
    },
    validate: (values: Partial<MainInfoFormState>) => {
      const errors: Record<string, string> = {};

      if (
        values.tin &&
        (!Number.isInteger(Number(values.tin)) || values.tin?.length !== 10)
      ) {
        errors.inn = `ИНН должен содержать 10 цифр`;
      }

      if (values.regNumber && !Number.isInteger(Number(values.regNumber))) {
        errors.regNumber = `ОГРН должен содержать только цифры`;
      }

      if (!values.shortName?.trim() && values.shortName !== undefined) {
        errors.shortName = `Поле обязательно для заполнения`;
      }

      if (!values.fullName?.trim() && values.fullName !== undefined) {
        errors.fullName = `Поле обязательно для заполнения`;
      }

      if (!values.address?.trim() && values.address !== undefined) {
        errors.address = `Поле обязательно для заполнения`;
      }

      if (
        values.bankTin &&
        (!Number.isInteger(Number(values.bankTin)) ||
          values.bankTin?.length !== 10)
      ) {
        errors.bankTin = `ИНН должен содержать 10 цифр`;
      }

      if (
        values.accountNumber &&
        !Number.isInteger(Number(values.accountNumber))
      ) {
        errors.accountNumber = `Расчетный счет должен содержать только цифры`;
      }

      if (values.corrNumber && !Number.isInteger(Number(values.corrNumber))) {
        errors.corrNumber = `Корр. счет банка должен содержать только цифры`;
      }

      if (!values.bankName?.trim()) {
        errors.bankName = `Поле обязательно для заполнения`;
      }

      return errors;
    },
  });

  const isEditable =
    formState.values.tin !== company?.tin ||
    formState.values.shortName !== company?.shortName ||
    formState.values.fullName !== company?.fullName ||
    formState.values.address !== company?.address ||
    formState.values.regNumber !== company?.regNumber ||
    formState.values.bankName !== bankCompany?.bankName ||
    formState.values.bik !== bankCompany?.bik ||
    formState.values.accountNumber !== bankCompany?.accountNumber ||
    formState.values.corrNumber !== bankCompany?.corrAccountNumber ||
    formState.values.bankTin !== (bankCompany?.tin || '');

  const getFieldError = (field: keyof MainInfoFormState) =>
    formState.errors[field] && isEditable ? (
      <div className="form-item__footer red">{formState.errors[field]}</div>
    ) : null;

  const setInitChanges = () => {
    formState.setValues(initialState);
  };

  useEffect(() => setInitChanges(), [company]);

  const handleCancelChanges = () => {
    setInitChanges();
    formState.validateForm(initialState);
    setReadOnly(true);
  };

  return (
    <form className="form-profile-company">
      <h3>Реквизиты компании:</h3>
      <div className="input-container">
        <UiInput
          value={formState.values.fullName}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          label="Название компании"
          readOnly={readOnly}
          name="fullName"
          copy
          lg
        />
        {getFieldError('fullName')}
      </div>
      <div className="input-container">
        <UiInput
          value={formState.values.shortName}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          label="Короткое название компании"
          readOnly={readOnly}
          name="shortName"
          copy
          lg
        />
        {getFieldError('shortName')}
      </div>
      <div className="form-row">
        <div className="input-container">
          <UiInput
            label="ИНН"
            readOnly
            name="tin"
            value={formState.values.tin}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.value?.length <= 10 && formState.handleChange(e)
            }
            onInputBlur={formState.handleBlur}
            copy
            lg
          />
          {getFieldError('tin')}
        </div>
        <div className="input-container">
          <UiInput
            label="ОГРН"
            readOnly
            name="regNumber"
            value={formState.values.regNumber}
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            copy
            lg
          />
          {getFieldError('regNumber')}
        </div>
      </div>
      <div className="input-container">
        <UiInput
          label="Юридический адрес"
          readOnly={readOnly}
          name="address"
          value={formState.values.address}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          copy
          lg
        />
        {getFieldError('address')}
      </div>
      <p className="p p_bold mb_24">Реквизиты счета:</p>
      <div className="input-container">
        <UiInput
          label="Банк"
          readOnly={readOnly}
          name="bankName"
          error={!readOnly && formState.errors.bankName}
          value={formState.values.bankName}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          copy
          lg
        />
        {getFieldError('bankName')}
      </div>
      <div className="input-container">
        <UiInput
          label="Расчетный счет"
          readOnly={readOnly}
          name="accountNumber"
          error={!readOnly && formState.errors.accountNumber}
          value={formState.values.accountNumber}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          copy
          lg
        />
        {getFieldError('accountNumber')}
      </div>
      <div className="form-row">
        <div className="input-container">
          <UiInput
            label="Корр. счет банка"
            readOnly={readOnly}
            name="corrNumber"
            error={!readOnly && formState.errors.corrNumber}
            value={formState.values.corrNumber}
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            copy
            lg
          />
          {getFieldError('corrNumber')}
        </div>
        <div className="input-container">
          <UiInput
            label="ИНН банка"
            readOnly={readOnly}
            name="bankTin"
            error={!readOnly && formState.errors.bankTin}
            value={formState.values.bankTin}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.value?.length <= 10 && formState.handleChange(e)
            }
            onInputBlur={formState.handleBlur}
            copy
            lg
          />
          {getFieldError('bankTin')}
        </div>
      </div>
      <div className="form-row">
        <div className="input-container">
          <UiInput
            label="БИК Банка"
            readOnly={readOnly}
            name="bik"
            error={readOnly && formState.errors.bik}
            value={formState.values.bik}
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            copy
            lg
          />
          {getFieldError('bik')}
        </div>
      </div>
      {readOnly && (
        <div className="mb_32">
          <IconBtn
            title="Восстановить ключ"
            icon={<SmallKeyIcon />}
            outlined
            sm
            onClick={() => {
              history.push('/restore-key');
            }}
          />
        </div>
      )}
      <div className="buttons">
        {readOnly && isAdmin && (
          <IconBtn
            title={'Редактировать'}
            onClick={() => setReadOnly(!readOnly)}
          />
        )}
        {!readOnly && isEditable && (
          <>
            <IconBtn
              disabled={
                loading ||
                (!formState.isValid &&
                  !formState.values.tin &&
                  !formState.values.shortName &&
                  !formState.values.fullName &&
                  !formState.values.address &&
                  !formState.values.regNumber)
              }
              title={'Сохранить изменения'}
              onClick={formState.handleSubmit}
            />
            <IconBtn
              title="Отменить"
              outlined
              onClick={handleCancelChanges}
            />
          </>
        )}
      </div>
    </form>
  );
};

export default MainTab;
